/**
 * PRICEBOOK-SEARCH-01 (Seth 2026-09-28: "16 x 25 x 1 filter merv" didn't bring up
 * L98-749 — "fix this across the board"). ONE search for every place Pegasus
 * picks from the pricebook: the job's equipment, invoice lines, proposals, plan
 * accessories and the plan builder, POs, the warehouse queue and the phone.
 *
 * The database does the work (the `pricebook_search` function):
 *   - dimensions collapse — "16 x 25 x 1" = "16X25X1";
 *   - part numbers match with or without dashes — "L98-749" = "l98749";
 *   - every word must be found somewhere (part #, description, model, brand,
 *     category, subcategory, vendor part #) — more words narrow the list;
 *   - best first: exact part # / model, then starts-with, then the whole phrase,
 *     then whole-word hits; in stock nudges up;
 *   - nothing found → close spellings ("honywell" finds Honeywell);
 *   - inactive and archived items stay hidden unless asked for.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Item } from "./items";

export interface PricebookFilters {
  category?: string | null;
  manufacturer?: string | null;
  equipType?: string | null;
  inStock?: boolean;
  includeInactive?: boolean;
  includeArchived?: boolean;
}

/** Search the pricebook — best matches first. An empty query browses (filters only). */
export async function searchPricebook(q: string, limit = 25, f: PricebookFilters = {}): Promise<Item[]> {
  const { data, error } = await supabase.rpc("pricebook_search", {
    p_q: q ?? "",
    p_limit: limit,
    p_include_inactive: !!f.includeInactive,
    p_category: f.category ?? null,
    p_manufacturer: f.manufacturer ?? null,
    p_equip_type: f.equipType ?? null,
    p_in_stock: !!f.inStock,
    p_include_archived: !!f.includeArchived,
  });
  if (error) throw error;
  return (data ?? []) as Item[];
}

/**
 * The search as a query. Runs once there are `minChars` typed (default 2), or with
 * an empty box when a filter scopes it (browsing a category). Keeps the last
 * results on screen while the next ones load, so the list doesn't flicker.
 */
export function usePricebookSearch(
  q: string,
  opts: PricebookFilters & { limit?: number; enabled?: boolean; minChars?: number; browse?: boolean } = {},
) {
  const term = q.trim();
  const min = opts.minChars ?? 2;
  const scoped = !!(opts.category || opts.manufacturer || opts.equipType || opts.inStock || opts.browse);
  return useQuery({
    queryKey: [
      "pricebook_search", term.toLowerCase(), opts.limit ?? 25, opts.category ?? null, opts.manufacturer ?? null,
      opts.equipType ?? null, !!opts.inStock, !!opts.includeInactive, !!opts.includeArchived,
    ],
    enabled: (opts.enabled ?? true) && (term.length >= min || (term.length === 0 && scoped)),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    queryFn: () => searchPricebook(term, opts.limit ?? 25, opts),
  });
}

/** The price a picker shows: the pricebook (sell) price. */
export function itemPrice(it: Pick<Item, "base_price">): number | null {
  return it.base_price != null ? Number(it.base_price) : null;
}

/** Our cost: the running average, else the last purchase. */
export function itemCostOf(it: Pick<Item, "average_cost" | "last_purchase">): number | null {
  const v = it.average_cost ?? it.last_purchase;
  return v != null ? Number(v) : null;
}
